export function useSubscriptionGuard(): MiddlewareGuard {
  const { t } = useFormatOptions();
  const appStore = useAppStore();
  const {
    isWizardInitialized,
    subscriptionStore,
    targetSubscription,
    sourceSubscription,
    currentSubscription,
    isCurrentSubscriptionActive,
    userCanManageSubscription,
    paymentInfoRequired,
    loadAvailablePlans,
    validatePaymentCheckoutSession,
    createPaymentCheckoutSession,
    clearCheckoutSessions,
  } = useSubscriptionApi();
  const { createSettingsPath } = useSettingsRoute();
  const {
    makeGuardResult,
    resolveSubscriptionRouteContext,
    createSubscriptionPath,
  } = useSubscriptionRoute();

  /**
   * Guard the route when inside the subscription flow
   * @return GuardResult
   */
  const guardFeatureRoute: FeatureGuard = async (route) => {
    const routeContext = resolveSubscriptionRouteContext(route);

    if (!routeContext.isRecognized) {
      devConsole.warn(
        `🚦 guard rule triggered: unrecognized route name for ${route.path}`,
        routeContext,
      );
      return makeGuardResult(route, "home");
    }
    await loadAvailablePlans();

    if (
      routeContext.matches.subscriptionFlow === "strict" &&
      routeContext.query[SESSION_ID_QUERY_PARAM]
    ) {
      const sessionId = routeContext.query[SESSION_ID_QUERY_PARAM] as string;
      devConsole.info(
        "🚦 guard rule triggered: evaluating session id query",
        sessionId,
      );

      const response = await validatePaymentCheckoutSession(sessionId);
      if (response.success) {
        return makeGuardResult(route, {
          redirect: "home",
        });
      }

      if (!("checkoutSession" in response)) {
        return makeGuardResult(route, {
          redirect: {
            path: createSubscriptionPath({
              wizardStep: "select-plan",
            }),
          },
        });
      }

      return makeGuardResult(route, {
        redirect: {
          path: createSubscriptionPath({
            wizardStep: "payment",
          }),
        },
      });
    }

    if (
      routeContext.matches.subscriptionFlow === "strict" ||
      routeContext.subscriptionWizardStep?.id === "select-plan"
    ) {
      return makeGuardResult(route, {
        redirect: createSubscriptionPath({
          wizardStep: "select-plan",
        }),
      });
    }

    if (!isWizardInitialized.value) {
      devConsole.error(
        "🚦 guard rule triggered: subscription store should have been initialized at this point, but it's not. Redirecting to the first step.",
      );
      return makeGuardResult(route, {
        redirect: createSubscriptionPath({
          wizardStep: "select-plan",
        }),
      });
    }

    if (routeContext.matches.paymentFlow) {
      const subscriptionId =
        targetSubscription.value?.price.id ??
        currentSubscription.value?.price?.id;

      const switchingTrial =
        sourceSubscription.value?.price.id !==
          targetSubscription.value?.price.id &&
        sourceSubscription.value?.isTrial;

      if (!subscriptionId) {
        devConsole.error(
          "🚦 guard rule triggered: no subscription id found for payment step",
          {
            targetSubscription: targetSubscription.value,
            currentSubscription: currentSubscription.value,
          },
        );
        return makeGuardResult(route, {
          redirect: createSubscriptionPath({
            wizardStep: "select-plan",
          }),
        });
      }

      const session = await createPaymentCheckoutSession(subscriptionId);
      if (!session) {
        devConsole.error(
          "🚦 guard rule triggered: failed to create checkout session for payment step",
          { subscriptionId },
        );
        return makeGuardResult(route, {
          redirect: createSubscriptionPath({
            wizardStep: "select-plan",
          }),
        });
      }

      if (targetSubscription.value?.plan.isFree || switchingTrial) {
        devConsole.warn(
          "🚦 guard rule triggered: No need for payment on free plan or switching during trial",
        );
        return makeGuardResult(route, {
          redirect: "home",
        });
      }
    }

    if (
      currentSubscription.value &&
      routeContext.subscriptionWizardStep?.id === "signup"
    ) {
      devConsole.error(
        "🚦 guard rule triggered: If already logged in, cannot access signup step",
      );
      return makeGuardResult(route, {
        redirect: createSubscriptionPath({
          wizardStep: "payment",
        }),
      });
    }
    return makeGuardResult(route, "continue");
  };

  /**
   * Guard the route when leaving the subscription flow
   * @param from
   * @param to
   * @return GuardResult
   */
  const guardExitRoute: ExitGuard = ({ from, to }) => {
    const fromRouteContext = resolveSubscriptionRouteContext(from);

    if (!fromRouteContext.isRecognized) return makeGuardResult(to, "continue");

    const toRouteContext = resolveSubscriptionRouteContext(to);

    const isLeavingPaymentStep =
      fromRouteContext.matches.paymentFlow &&
      !toRouteContext.matches.paymentFlow;

    if (isLeavingPaymentStep) {
      devConsole.info(
        "🚦 guard rule triggered: leaving payment step, clearing checkout sessions",
      );
      clearCheckoutSessions();
    }

    const isLeavingWizardFlow =
      fromRouteContext.isInSubscriptionWizardFlow &&
      (!toRouteContext.isInSubscriptionWizardFlow ||
        toRouteContext.subscriptionWizardStep?.id === "select-plan");

    // should confirm if navigating away from subscription steps and user has unsaved changes
    if (isLeavingWizardFlow) {
      if (
        subscriptionStore.confirmUnsavedChangesBeforeLeaving(
          toRouteContext.localizedPath,
        )
      ) {
        devConsole.warn(
          "🚦 middleware rule triggered: there are subscription unsaved changes, showing a confirmation dialog",
        );
        return makeGuardResult(to, "abort");
      }

      devConsole.info(
        "🚦 middleware rule triggered: Resetting subscription store as user is going out of subscription page",
      );
      subscriptionStore.reset();
    }

    return makeGuardResult(to, "continue");
  };

  /**
   * @return GuardResult
   */
  const guardRouteAccess: AccessGuard = ({ to, authFlow }) => {
    const routeContext = resolveSubscriptionRouteContext(to);

    if (!routeContext.isRecognized) return makeGuardResult(to, "abort");

    if (!isCurrentSubscriptionActive.value) {
      if (routeContext.requiresActiveSubscription) {
        devConsole.warn(
          "🚦 guard rule triggered: user with inactive subscription trying to access content that requires an active subscription",
          {
            currentSubscription: currentSubscription.value,
            userCanManageSubscription: userCanManageSubscription.value,
            isCurrentSubscriptionActive: isCurrentSubscriptionActive.value,
            paymentInfoRequired: paymentInfoRequired.value,
            requiresActiveSubscription: routeContext.requiresActiveSubscription,
          },
        );

        return makeGuardResult(to, {
          redirect: createSettingsPath(),
        });
      }

      return makeGuardResult(to, "continue");
    }

    if (
      authFlow === "post-login" &&
      paymentInfoRequired.value &&
      userCanManageSubscription.value
    ) {
      if (isWizardInitialized.value && !routeContext.matches.paymentFlow) {
        devConsole.warn(
          "🚦 guard rule triggered: right after login user should continue with the subscription wizard",
        );
        return makeGuardResult(to, {
          redirect: createSubscriptionPath({
            wizardStep: "payment",
          }),
        });
      }

      if (!isWizardInitialized.value && !routeContext.matches.settingsFlow) {
        devConsole.warn(
          "🚦 guard rule triggered: right after login user should be taken to update the payment method",
        );
        return makeGuardResult(to, {
          redirect: createSettingsPath({
            section: "payment-method",
          }),
        });
      }
    }

    // Outside of the subscription flow
    if (!routeContext.matches.subscriptionFlow) {
      return makeGuardResult(to, "continue");
    }

    if (!currentSubscription.value) {
      devConsole.error(
        "🚦 guard rule triggered: this should never happen, an existing user with no subscription",
      );

      appStore.addError(t("cksetup.subscription.noSubscriptionErrorMessage"));

      return makeGuardResult(to, {
        redirect: createSubscriptionPath({
          error: true,
        }),
      });
    }

    if (!userCanManageSubscription.value) {
      devConsole.warn(
        "🚦 guard rule triggered: user is trying to access subscription wizard without proper permissions",
      );
      return makeGuardResult(to, "home");
    }

    return makeGuardResult(to, "continue");
  };

  return {
    name: "subscriptionGuard",
    guardFeatureRoute,
    guardExitRoute,
    guardRouteAccess,
  };
}
